import { Component, DestroyRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { ColumnModel, FilterSettingsModel, GridComponent, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import { forkJoin } from 'rxjs';
import * as XLSX from 'xlsx';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { KmlService } from '../../../../../@core/utils/kml.service';
import { ConfirmationData, ConfirmationDialogComponent } from '../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { SessionService } from '../../../../../@core/utils/session.service';
import { Customer } from '../../../../modules/contracts/models/customer';
import { CustomerService } from '../../../../modules/contracts/services/customer.service';
import { FarmDTO } from '../../../models/farm';
import { TenantWordsPipe } from '../../../pipes/tenant-words.pipe';
import { FarmsService } from '../../../services/farms.service';
import { KwFilterDropdownService } from '../../../widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { FilterResults } from '../../../widgets/kw-filter-dropdown/models/filter-results';
import { FarmsTableDataSource } from './farms-table-datasource';
import { FarmDetailComponent } from '../farm-detail/farm-detail.component';

@Component({
  selector: 'kw-farms-table',
  templateUrl: './farms-table.component.html',
  styleUrls: ['./farms-table.component.css']
})
export class FarmsTableComponent implements OnInit {
  @Output() loaded = new EventEmitter();

  private destroyRef = inject(DestroyRef);
  //#region Properties
  public allFarms: FarmDTO[] = [];
  public savingFarmIds: Set<number> = new Set<number>();
  public highlightedFarmId: number = null;
  private pendingScrollToFarmId: number = null;
  public isLoading: boolean;
  public faCheck = faCheck;
  public faTimes = faTimes;
  public enablePersistence: boolean = true;
  public filterCount: number = 0;
  public filterResults: FilterResults;
  dataSource: FarmsTableDataSource;
  public pageSettings: PageSettingsModel = {
    pageSize: 200,
    pageSizes: [50, 100, 200],
  };
  public customersList: Customer[];
  public units: string[] = ["gal", "L", "qt", "mL", "fl oz"];
  public filterSettings: FilterSettingsModel = {
    mode: "Immediate",
    type: "Excel",
  };
  public toolbarOptions: string[] = [
    "ExcelExport",
    "KML Export",
    "JSON Export",
  ];
  public allColumns: ColumnModel[] = [];
  public gridName: string = "BlocksConfigFilters";
  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  displayedColumns = ['name', 'blocks', 'address', 'city', 'state', 'zip', 'integrationId', 'active', 'edit', 'customer'];
  //#endregion

  //#region Icon Properties
  public faPen = faPen;
  public faTrash = faTrash;
  //#endregion

  //#region ViewChild Properties
  @ViewChild(MatPaginator, { static: false })
  paginator: MatPaginator;

  @ViewChild(MatSort, { static: false })
  sort: MatSort;

  @ViewChild("grid", { static: true })
  public grid: GridComponent;

  @ViewChild("farmActionTemplate", { static: true })
  public farmActionTemplate: any;

  @ViewChild("farmNameTemplate", { static: true })
  public farmNameTemplate: any;
  //#endregion

  //#region Constructor with dependencies
  constructor(
    private farmService: FarmsService,
    private errorService: ErrorService,
    private kmlService: KmlService,
    private dialogService: MatDialog,
    private toastrService: NbToastrService,
    private tenantWordPipe: TenantWordsPipe,
    private customersService: CustomerService,
    private session: SessionService,
    private filterService: KwFilterDropdownService,
    private router: Router,
  ) { }
  //#endregion

  ngOnInit() {
    this.loadData();
    this.getDropDown();
  }

  loadData() {
    return new Promise((resolve, reject) => {
      // let self = this;
      this.isLoading = true;
      forkJoin([
        this.farmService.apiFarmsGet(),
      ]).subscribe((x) => {
        this.allFarms = (x[0] || []).sort((a, b) => {
          const an = a && a.name ? a.name.toString().toLowerCase() : '';
          const bn = b && b.name ? b.name.toString().toLowerCase() : '';
          return an.localeCompare(bn);
        });

        this.setColumns();
        this.isLoading = false;
        this.loaded.emit(false);
      }),
        (err) => {
          this.errorService.handleError(err);
          this.isLoading = false;
          this.loaded.emit(false);
        };
    });
  }

  toLifecycle(farm: FarmDTO) {
    this.router.navigateByUrl(`harvest-engine/core/farms/lifecycle/${farm.id}`);
  }

  setColumns() {
    this.allColumns = [];

    // Hidden primary key column - lets us look up/select a specific row
    // (e.g. to scroll to and highlight a newly created farm).
    this.allColumns.push({
      field: "id",
      isPrimaryKey: true,
      visible: false,
    });

    this.allColumns.push(
      ...[
        {
          field: "name",
          headerText: "Name",
          template: this.farmNameTemplate,
        },
        {
          field: "customerName",
          headerText: this.tenantWordPipe.transform("Customer"),
        },
        {
          field: "streetAddress",
          headerText: "Address",
        },
        {
          field: "city",
          headerText: "City",
        },
        {
          field: "state",
          headerText: "State",
        },
        {
          field: "zipCode",
          headerText: "Zip",
        },
        {
          field: "integrationId",
          headerText: "Integration Id",
        },
        {
          headerText: "Action",
          template: this.farmActionTemplate,
          width: 200,
        }
      ]
    );
  }

  toolbarClick(args) {
    this.isLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.item.properties.text == "KML Export") {
      this.kmlService.getAllKML(new Date().getFullYear());
      this.isLoading = false;
    } else if (args.item.properties.text == "JSON Export") {
      this.kmlService.getGeoJSONPolygons();
      this.isLoading = false;
    } else {
      this.grid
        .excelExport({
          fileName: `FarmsExport.xlsx`,
        })
        .then(() => {
          this.isLoading = false;
        });
    }
  }
  clearFilters() {
    window.localStorage.setItem("grid" + this.gridName, "");
    this.grid.clearFiltering();
    this.filterCount = 0;
  }

  onActionComplete(args: any) {
    if (args.requestType === 'filtering') {
      this.updateFilterCount();
    }
  }

  onGridDataBound() {
    this.updateFilterCount();

    if (this.pendingScrollToFarmId != null) {
      // goToPage() can fire this from a render that still has stale data
      // (queued just ahead of the refresh that actually adds the row), so
      // only clear the pending id once we've actually found and scrolled to
      // it - otherwise keep waiting for the next dataBound to try again.
      const id = this.pendingScrollToFarmId;
      const rowIndex = this.grid.getRowIndexByPrimaryKey(id);
      if (rowIndex > -1) {
        this.scrollGridRowIntoView(this.grid.getRowByIndex(rowIndex));
        this.pendingScrollToFarmId = null;
        this.startHighlightFade(this.grid.getRowByIndex(rowIndex), id);

        setTimeout(() => {
          const idx = this.grid.getRowIndexByPrimaryKey(id);
          if (idx > -1) {
            const row = this.grid.getRowByIndex(idx);
            this.scrollGridRowIntoView(row);
            this.startHighlightFade(row, id);
          }
        }, 200);
      }
    }
  }

  private updateFilterCount() {
    const cols = (this.grid && this.grid.filterSettings && this.grid.filterSettings.columns) ? this.grid.filterSettings.columns : [];
    const uniqueFields = cols.map(c => c.field as string).filter((f: string, i: number, a: string[]) => a.indexOf(f) === i);
    this.filterCount = uniqueFields.length;
  }

  addFarmClicked(isEdit: boolean, farm?: FarmDTO): void {
    const farmEditDialog = this.dialogService.open(FarmDetailComponent, {
      width: "1200px"
    });

    if (isEdit) {
      // Clone: never hand the dialog a live reference into allFarms, or its
      // two-way-bound form fields would mutate the grid row before Save.
      this.session.selectedFarm = { ...farm };
    }

    farmEditDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result && result.backgroundSave && result.farm) {
        if (result.isNew) {
          this.createFarmInBackground(result.farm);
        } else {
          this.saveFarmInBackground(result.farm);
        }
      } else if (result) {
        this.loadData();
      }
      this.session.selectedFarm = {};
    });
  }

  private saveFarmInBackground(farm: FarmDTO): void {
    const originalFarm = this.allFarms.find((f) => f.id === farm.id);
    this.savingFarmIds.add(farm.id);
    this.insertOrReplaceFarmRow(this.shapeFarmForGrid(farm, originalFarm));
    this.refreshGridSoon();

    this.farmService.apiFarmsByIdPut(farm.id, farm).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updated) => {
        this.savingFarmIds.delete(farm.id);
        // Farm names in the filter come from the blocks' copy - keep it in sync so other pages show the rename
        this.filterService.syncFarm(updated || farm);
        // 204 No Content leaves `updated` null/undefined - fall back to the farm we sent.
        this.insertOrReplaceFarmRow(this.shapeFarmForGrid(updated || farm, originalFarm));
        this.refreshGridSoon();
        this.toastrService.success(
          "Farm details successfully updated!",
          farm.name + " updated!"
        );
      },
      error: (err) => {
        this.savingFarmIds.delete(farm.id);
        if (originalFarm) {
          this.insertOrReplaceFarmRow(originalFarm);
        }
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to update Farm "' + farm.name + '". Please try again.',
          "Save failed"
        );
      }
    });
  }

  private createFarmInBackground(farm: FarmDTO): void {
    // The farm doesn't exist server-side yet, so show a temporary placeholder
    // row (negative id so it can't collide with a real one) with the saving
    // spinner until the server assigns a real id.
    const tempId = -Date.now();
    this.insertOrReplaceFarmRow(this.shapeFarmForGrid({ ...farm, id: tempId }));
    this.allFarms = this.sortFarmsByName(this.allFarms);
    this.savingFarmIds.add(tempId);
    this.refreshGridSoon();

    this.farmService.apiFarmsPost(farm).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        this.removeFarmRow(tempId);
        this.savingFarmIds.delete(tempId);
        this.insertOrReplaceFarmRow(this.shapeFarmForGrid(created || farm));
        this.allFarms = this.sortFarmsByName(this.allFarms);
        this.refreshGridSoon();
        this.jumpToFarmRow((created && created.id) || farm.id);
        this.toastrService.success(
          ((created && created.name) || farm.name) + " created!",
          "Farm successfully created!"
        );
      },
      error: (err) => {
        this.removeFarmRow(tempId);
        this.savingFarmIds.delete(tempId);
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          "Failed to create " + (farm.name || "farm") + ". Please try again.",
          "Create failed"
        );
      }
    });
  }

  private shapeFarmForGrid(updated: FarmDTO, original?: FarmDTO): FarmDTO {
    const matchedCustomer = this.customersList && this.customersList.find((c) => c.id === updated.customerId);
    return {
      ...updated,
      customerName: (matchedCustomer && matchedCustomer.name) || updated.customerName || (original && original.customerName)
    };
  }

  private insertOrReplaceFarmRow(farm: FarmDTO): void {
    const index = this.allFarms.findIndex((f) => f.id === farm.id);
    const next = this.allFarms.slice();
    if (index !== -1) {
      next[index] = farm;
    } else {
      next.push(farm);
    }
    this.allFarms = this.sortFarmsByName(next);
  }

  private removeFarmRow(id: number): void {
    this.allFarms = this.allFarms.filter((f) => f.id !== id);
  }

  private compareFarmNames(a: string, b: string): number {
    const an = a ? a.toString().trim().toLowerCase() : '';
    const bn = b ? b.toString().trim().toLowerCase() : '';
    return an.localeCompare(bn);
  }

  private sortFarmsByName<T extends { name?: string }>(list: T[]): T[] {
    return list.sort((a, b) => this.compareFarmNames(a && a.name, b && b.name));
  }

  private refreshGridSoon(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.refresh();
      }
    });
  }

  // Applies the green highlight directly (and times its removal) right when
  // the row is actually visible, instead of leaving rowDataBound to start
  // the clock the first time it happens to touch this row - which could be
  // well before the row is scrolled into view.
  private startHighlightFade(row: Element, id: number): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    rowElement.classList.add('row-highlight-new');
    if (this.highlightedFarmId === id) {
      this.highlightedFarmId = null;
    }
    setTimeout(() => {
      rowElement.classList.remove('row-highlight-new');
    }, 2000);
  }

  // Scrolls the grid's own internal content viewport, not the page - a
  // plain row.scrollIntoView() walks every scrollable ancestor (including
  // the page), which in practice was moving the page while leaving the
  // grid's own internal scroll area untouched, so the row stayed hidden.
  private scrollGridRowIntoView(row: Element): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    const scrollableContent = this.grid.getContent().querySelector('.e-content') as HTMLElement;
    if (scrollableContent) {
      const targetScrollTop = rowElement.offsetTop
        - (scrollableContent.clientHeight / 2)
        + (rowElement.offsetHeight / 2);
      scrollableContent.scrollTo({ top: Math.max(targetScrollTop, 0), behavior: 'auto' });
    } else if (typeof rowElement.scrollIntoView === 'function') {
      rowElement.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
  }

  private isDefaultSortActive(): boolean {
    const cols = (this.grid && this.grid.sortSettings && this.grid.sortSettings.columns) || [];
    if (cols.length === 0) {
      return true;
    }
    return cols.length === 1 && cols[0].field === 'name' && cols[0].direction === 'Ascending';
  }

  private jumpToFarmRow(id: number): void {
    this.highlightedFarmId = id;
    // Scrolling happens once the grid actually finishes its next render
    // (see onGridDataBound) rather than after a guessed delay.
    this.pendingScrollToFarmId = id;

    if (!this.grid || !this.isDefaultSortActive()) {
      // Can't reliably compute which page the row lands on under a custom
      // sort - it'll still get highlighted/scrolled to whenever it's next
      // rendered on whatever page the user navigates to.
      return;
    }

    // Defer past the refresh(es) already queued by insertOrReplaceFarmRow/
    // removeFarmRow so this pages using the grid's up-to-date data.
    setTimeout(() => {
      const index = this.allFarms.findIndex((f) => f.id === id);
      if (index === -1) {
        return;
      }

      const pageSize = this.pageSettings.pageSize || this.allFarms.length;
      const page = Math.floor(index / pageSize) + 1;

      if (this.grid.pageSettings.currentPage !== page) {
        this.grid.goToPage(page);
      }
    });
  }

  onRowDataBound(args: any): void {
    if (args.data && this.savingFarmIds.has(args.data.id)) {
      args.row.classList.add('row-saving');
    } else if (args.row) {
      args.row.classList.remove('row-saving');
    }
    if (args.data && this.highlightedFarmId != null && args.data.id === this.highlightedFarmId) {
      args.row.classList.add('row-highlight-new');
    }
  }

  confirmDelete(id: number) {
    let self = this;
    const confirmDialogData: ConfirmationData = {
      title: "Confirm Deletion",
      confirmMessage: "Are you sure you want to delete this Farms?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      displayComment: false,
      isWarning: false,
    };

    const confirmDialog = this.dialogService.open(ConfirmationDialogComponent, {
      width: "400px",
      data: confirmDialogData,
    });

    confirmDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result === true) {
        self.isLoading = true;
        this.deleteRec(id);
      }
    });
  }

  deleteRec(id: number) {
    this.farmService.apiFarmsByIdDelete(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {next:() => {
        this.toastrService.success("Farms successfully deleted!");
        // setTimeout(() => {
        // this.isLoading = false;
        this.loadData();
        // }, 1000);
      },
      error:(error) => {
        this.errorService.handleError(error);
        console.error(error);
      }}
    );
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    this.isLoading = true;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const workbook = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const headerRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true }) as any[][];
      const originalHeaders: string[] = (headerRows && headerRows.length > 0)
        ? headerRows[0].map((h: any) => (h == null ? '' : h.toString()))
        : [];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: true });

      const seenNames = new Set<string>();
      const promises = jsonData.map((row) => this.processRow(row, seenNames));
      this.FarmsImportResults(promises, originalHeaders);
    };

    reader.onerror = (err) => this.errorService.handleError(err);
    reader.readAsArrayBuffer(file);
  }

  private processRow(element: any, seenNames: Set<string>): Promise<any> {
    // Safe getter
    const get = (key: string) => (element[key] ? element[key] : "").toString().trim();

    // 1. NAME VALIDATION
    const name = get("Name*");
    if (!name) return this.fail(element, "Please Add Name.");

    // 2. DUPLICATE CHECK
    const nameKey = name.toLowerCase();
    if (seenNames.has(nameKey) ||
        this.allFarms.some(f => (f.name || "").trim().toLowerCase() === nameKey))
      return this.fail(element, "Duplicate");

    // Raw values
    const rawTankSize = get("Default Tank Size");
    const rawVolume = get("Default Volume");
    // Validate BEFORE conversion
    if (rawTankSize && isNaN(Number(rawTankSize))) 
      return this.fail(element, "Please Add number in Default Tank Size.");

    if (rawVolume && isNaN(Number(rawVolume))) 
      return this.fail(element, "Please Add number in Default Volume.");

    // 3. BUILD FARM OBJECT
    const obj: FarmDTO = {
      isActive: true,
      name,
      streetAddress: get("Street Address"),
      city: get("City"),
      state: get("State"),
      zipCode: get("Zip Code"),
      email: get("Email"),
      defaultTankSize: Number(element["Default Tank Size"]) || 0,
      defaultVolume: Number(element["Default Volume"]) || 0,
      integrationId: get("Integration Id"),
      notificationsEnabled: this.toBoolean(get("Send Notifications")),
    };
    // 4. CUSTOMER MATCHING (optional)
    const customerName = get("Customer Name");
    if (customerName) {
      const target = customerName.toLowerCase();
      const match = this.customersList.find(c => (c.name || "").toLowerCase() === target);

      if (!match) return this.fail(element, "Please Add Valid Customer Name.");

      obj.customerId = match.id;
      obj.customerName = match.name;
    }

    // 5. UNIT VALIDATIONS
    const tankUnit = get("Default Tank Size Unit");
    if (tankUnit && !this.unitExists(tankUnit))
      return this.fail(element, "Please Add Valid Default Tank Unit.");
    if (tankUnit) obj.defaultTankSizeUnit = tankUnit;

    const volUnit = get("Default Volume Unit");
    if (volUnit && !this.unitExists(volUnit))
      return this.fail(element, "Please Add Valid Default Volume Unit.");
    if (volUnit) obj.defaultVolumeUnit = volUnit;

    // 6. SAVE
    seenNames.add(nameKey);
    return new Promise(resolve => {
      this.farmService.apiFarmsPost(obj).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          element["RESULT"] = "Success";
          resolve(element);
        },
        error: (err) => {
          this.errorService.handleError(err);
          element["RESULT"] = "Error saving: " + err.message;
          resolve(element);
        },
      });
    });
  }

  private fail(element: any, msg: string): Promise<any> {
    element["RESULT"] = msg;
    return Promise.resolve(element);
  }

  private unitExists(unit: string): boolean {
    return this.units.some(u => u.toLowerCase() === unit.toLowerCase());
  }


  FarmsImportResults(promises, originalHeaders: string[] = []) {
    // Wait for all operations to complete before generating Excel
    Promise.all(promises).then((results) => {
      results = this.sortExportResult(results)
      // Create the results workbook
      const resultWorkbook = XLSX.utils.book_new();
      const headerList = this.buildResultHeaders(originalHeaders, results);
      const resultSheet = XLSX.utils.json_to_sheet(results, { header: headerList });
      XLSX.utils.book_append_sheet(resultWorkbook, resultSheet, 'Farms Report');
      XLSX.writeFile(resultWorkbook, 'FarmsImportResults.xlsx');

      this.isLoading = false;
      this.loadData(); // Refresh the grid
    }).catch(error => {
      console.error(error);
      this.errorService.handleError(error);
      this.isLoading = false;
    });
  }

  private buildResultHeaders(originalHeaders: string[], results: any[]): string[] {
    const headers = (originalHeaders || []).filter(h => h && h !== 'RESULT');
    const seen = new Set(headers);
    (results || []).forEach(row => {
      Object.keys(row || {}).forEach(k => {
        if (k !== 'RESULT' && !seen.has(k)) { seen.add(k); headers.push(k); }
      });
    });
    headers.push('RESULT');
    return headers;
  }

  toBoolean(val: any): boolean {
    if (val === null || val === undefined) return false;

    // If it's already a number
    if (typeof val === "number") return val === 1;

    // Convert to string and trim safely
    const str = String(val).toString().trim().toLowerCase();
    return str === "yes" || str === "true" || str === "active" || str === "1";
  }

  sortExportResult(list) {
    const resultPriority = {
      // Priority 1: Messages starting with "Error saving:"
      "Error saving:": 1,
      // Priority 2: Exact matches
      "Please Add Name.": 2,
      "Please Add number in Default Volume.": 3,
      "Please Add number in Default Tank Size.": 4,
      "Please Add Valid Default Volume Unit.": 5,
      "Please Add Valid Default Tank Unit.": 6,
      "Please Add Valid Customer Name.": 7,
      "Duplicate": 8,
      "Please Add number in Credit Limit.": 9,
      "Success": 10,
      // Default high number for unexpected messages (puts them last)
      "default": 99
    };

    return list.sort((a, b) => {
      const resultA = a.RESULT.toString().trim();
      const resultB = b.RESULT.toString().trim();

      // Helper function to get priority based on exact match or startsWith check
      const getPriority = (resultMsg) => {
        // Check for the highest priority message type first
        if (resultMsg.startsWith("Error saving:")) {
          return resultPriority["Error saving:"];
        }

        // Check for exact matches
        return resultPriority[resultMsg] || resultPriority.default;
      };

      const priorityA = getPriority(resultA);
      const priorityB = getPriority(resultB);

      // Standard comparison logic:
      // priorityA < priorityB returns a negative number, so 'a' comes before 'b'
      return priorityA - priorityB;
    });
  }

  getDropDown() {
    this.customersService.getCustomerList().subscribe((x) => {
      this.customersList = x.filter(f => f.name);
    })
  }
  downloadFiles(args: string){
    this.isLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.includes("KML")) {
      this.kmlService.getAllKML(new Date().getFullYear());
      this.isLoading = false;
    } else if (args.includes("JSON")) {
      this.kmlService.getGeoJSONPolygons();
      this.isLoading = false;
    } else {
      this.grid
        .excelExport({
          fileName: `FarmsExport.xlsx`,
        })
        .then(() => {
          this.isLoading = false;
        });
    }
  }
}
